import { DiscountUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * MENINA first-order discount offer controller (email-gated).
 *
 * Submitting an email subscribes the customer to the Klaviyo Master List AND
 * applies THEAGENDA to the cart. The submit IS the claim — the discount is
 * never applied without it. The card is server-rendered by
 * snippets/cart-offer.liquid and mirrors the keyring's cart-gift.js.
 *
 * ── Why this is more than a form ───────────────────────────────────────────
 * The welcome email and Klaviyo popup link to `/discount/THEAGENDA`, which
 * makes Shopify hold the code at SESSION level and auto-apply it to the cart
 * before any theme code runs. So we continuously reconcile two states:
 *
 *   STRIP   — code on the cart with no claim marker => auto-applied, not
 *             claimed. Remove it so the form comes back.
 *   UNCLAIM — claim marker but no code (cart emptied, discount removed). The
 *             marker is stale; clear it, or the next auto-apply reads as
 *             pre-claimed and is never stripped. That leak silently defeated
 *             an earlier version of this fix: cart attributes survive
 *             `cart/clear.js` and live forever unless explicitly cleared.
 *
 * Reconciliation reads the LIVE cart (`/cart.js`), never the rendered DOM,
 * which is stale for a beat after we mutate the cart.
 *
 * ⚠️ Scheduling uses setTimeout, NEVER requestAnimationFrame: rAF does not fire
 * while the document is hidden, and cmd-clicking the welcome email opens
 * exactly that — a background tab. With rAF the reconcile never ran there, and
 * it also made testing lie (a "stripped!" pass where nothing had run at all).
 */

const FORM_SELECTOR = '[data-cart-offer-form]';
const CLAIM_ATTR = '_offer_claim';

/** Klaviyo. The public key is designed to be exposed in the storefront. */
const KLAVIYO_COMPANY_ID = 'XbsqCC';
const KLAVIYO_LIST_ID = 'VLK4JW'; // Master List — triggers "MENINA — Welcome Series"
const KLAVIYO_REVISION = '2024-10-15';
const KLAVIYO_SOURCE = 'Cart first-order offer';

/** Serialises reconcile passes; never blocks a submit. */
let reconciling = false;

/**
 * Defer past the cart section re-render. See the rAF warning above.
 * @param {() => void} fn
 */
function schedule(fn) {
  setTimeout(fn, 50);
}

/** @param {object} body - cart/update.js payload */
async function updateCart(body) {
  const response = await fetch(Theme.routes.cart_update_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  return response.json();
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`);
  return response.json();
}

/**
 * @param {object} cart - a cart.js payload
 * @param {string} code
 */
function isApplied(cart, code) {
  return (cart.discount_codes || []).some(
    (/** @type {{code: string, applicable: boolean}} */ d) => d.code === code && d.applicable !== false
  );
}

/**
 * @param {object} cart - a cart.js payload
 * @param {string} code
 */
function isClaimed(cart, code) {
  return Boolean(cart.attributes && cart.attributes[CLAIM_ATTR] === code);
}

/** @param {string} email */
function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
}

/**
 * Subscribe the address to the Master List, recording marketing consent.
 * Resolves false on failure — a Klaviyo outage must not cost the customer the
 * discount they just gave their address for.
 *
 * @param {string} email
 * @returns {Promise<boolean>}
 */
async function subscribe(email) {
  try {
    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: KLAVIYO_SOURCE,
              // Only `email` here. A `subscriptions` block is rejected by this
              // endpoint at this revision ("'subscriptions' is not a valid field
              // for the resource 'profile'", HTTP 400) — POSTing to
              // /client/subscriptions/ IS the consent action, so opting them in
              // to the list is implicit. Do not re-add it.
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: KLAVIYO_LIST_ID } } },
          },
        }),
      }
    );

    if (!response.ok) {
      console.error('[cart-offer] klaviyo subscribe failed', response.status, await response.text());
      return false;
    }
    return true;
  } catch (error) {
    console.error('[cart-offer] klaviyo subscribe error', error);
    return false;
  }
}

/**
 * Tie the browsing session to the profile so Klaviyo's abandoned-cart flow
 * knows who this is. klaviyo.js processes the queue once loaded, so pushing
 * early is safe (same pattern as snippets/klaviyo-added-to-cart.liquid).
 * @param {string} email
 */
function identify(email) {
  try {
    window.klaviyo = window.klaviyo || [];
    window.klaviyo.push(['identify', { $email: email }]);
  } catch (error) {
    console.error('[cart-offer] klaviyo identify error', error);
  }
}

/**
 * Apply the code and mark it claimed in ONE call, so no reload can observe the
 * code without its marker (which would strip it).
 *
 * @param {string} code
 * @returns {Promise<boolean>}
 */
async function applyDiscount(code) {
  const cart = await fetchCart();
  const existing = (cart.discount_codes || []).map((/** @type {{code: string}} */ d) => d.code);
  const codes = existing.includes(code) ? existing : [...existing, code];

  const data = await updateCart({
    discount: codes.join(','),
    attributes: { [CLAIM_ATTR]: code },
  });

  if (!isApplied(data, code)) {
    // Roll back both, so a dead code can't leave a claim behind.
    await updateCart({
      discount: existing.filter((/** @type {string} */ c) => c !== code).join(','),
      attributes: { [CLAIM_ATTR]: '' },
    });
    return false;
  }

  document.dispatchEvent(new DiscountUpdateEvent(data, 'cart-offer'));
  return true;
}

/**
 * @param {HTMLElement} host - the <cart-offer> element
 * @param {HTMLFormElement} form
 */
async function submitOffer(host, form) {
  const button = form.querySelector('[data-cart-offer-submit]');
  const input = /** @type {HTMLInputElement | null} */ (
    form.querySelector('[data-cart-offer-email]')
  );
  const errorEl = form.querySelector('[data-cart-offer-error]');
  if (!(button instanceof HTMLElement) || !input || !(errorEl instanceof HTMLElement)) return;
  if (button.hasAttribute('disabled')) return;

  /** @param {string} message */
  const fail = (message) => {
    errorEl.textContent = message;
    errorEl.classList.remove('hidden');
  };

  errorEl.classList.add('hidden');

  const code = host.dataset.code;
  const email = input.value.trim();

  if (!isValidEmail(email)) {
    fail(host.dataset.errorEmail || 'Check the email address.');
    input.focus();
    return;
  }
  if (!code) return;

  const restoreLabel = button.textContent;
  button.setAttribute('disabled', '');
  button.textContent = host.dataset.pendingLabel || 'ADDING';

  try {
    // Capture first, then discount. A Klaviyo failure is logged but never
    // blocks the discount — the customer has already done their part.
    await subscribe(email);
    identify(email);

    const applied = await applyDiscount(code);
    if (!applied) fail(host.dataset.errorGeneric || 'The discount could not be applied.');
  } catch (error) {
    console.error('[cart-offer] submit error', error);
    fail(host.dataset.errorGeneric || 'The discount could not be applied.');
  } finally {
    button.removeAttribute('disabled');
    if (restoreLabel) button.textContent = restoreLabel;
  }
}

/**
 * Keep the cart honest: the code is only ever on it because the customer
 * submitted for it. Reads the live cart, so it cannot loop.
 */
async function reconcile() {
  if (reconciling) return;

  const host = /** @type {HTMLElement | null} */ (document.querySelector('cart-offer[data-code]'));
  const code = host?.dataset.code;
  if (!code) return;

  // Cheap bail: only look closer if the server flagged something this render.
  if (host.dataset.shouldStrip !== 'true' && host.dataset.shouldClearClaim !== 'true') return;

  reconciling = true;
  try {
    const cart = await fetchCart();
    const applied = isApplied(cart, code);
    const claimed = isClaimed(cart, code);

    if (applied && !claimed) {
      const remaining = (cart.discount_codes || [])
        .map((/** @type {{code: string}} */ d) => d.code)
        .filter((/** @type {string} */ c) => c !== code);
      const data = await updateCart({ discount: remaining.join(',') });
      document.dispatchEvent(new DiscountUpdateEvent(data, 'cart-offer'));
    } else if (!applied && claimed) {
      // Stale marker => clear it so the next auto-apply is stripped, not
      // mistaken for a claim. No re-render: the card already shows the form.
      await updateCart({ attributes: { [CLAIM_ATTR]: '' } });
    }
  } catch (error) {
    console.error('[cart-offer] reconcile error', error);
  } finally {
    reconciling = false;
  }
}

if (!window.__cartOfferWired) {
  window.__cartOfferWired = true;

  document.addEventListener('submit', (event) => {
    const target = event.target;
    const form = target instanceof Element ? target.closest(FORM_SELECTOR) : null;
    if (!(form instanceof HTMLFormElement)) return;
    const host = /** @type {HTMLElement | null} */ (form.closest('cart-offer'));
    if (!host) return;
    event.preventDefault();
    submitOffer(host, form);
  });

  document.addEventListener(ThemeEvents.cartUpdate, () => {
    schedule(reconcile);
  });
  document.addEventListener(ThemeEvents.discountUpdate, () => {
    schedule(reconcile);
  });

  // Catch the arrive-from-welcome-email case on initial load.
  schedule(reconcile);
}

if (!customElements.get('cart-offer')) {
  customElements.define('cart-offer', class extends HTMLElement {});
}
